import { z } from 'zod';

// Roles match src/data.jsx ROLES exactly. The `can` array uses the same
// resource keys the prototype's UI uses (e.g. 'clients', 'engagements:read').
// Wildcards: '*' = everything; '*:read' = read on every resource.

export const ROLE_IDS = [
  'super',
  'admin',
  'lead',
  'analyst',
  'compliance',
  'finance',
  'readonly',
] as const;

export const RoleIdSchema = z.enum(ROLE_IDS);
export type RoleId = z.infer<typeof RoleIdSchema>;

// Resource keys referenced in the prototype:
//   clients, engagements, users, invoices, reports, settings, messages,
//   kyc, audit, engagements:read, reports:write, reports:read, clients:read
// Wildcard rules: '*' (all), '<resource>:read' (read-only on a resource),
// '*:read' (read everything).
export const RolePermissionSchema = z.string().min(1);
export type RolePermission = z.infer<typeof RolePermissionSchema>;

export const RoleSchema = z.object({
  id: RoleIdSchema,
  label: z.string(),
  can: z.array(RolePermissionSchema).min(1),
});
export type Role = z.infer<typeof RoleSchema>;

// Action shape used by the RBAC middleware:
//   { resource: 'clients', action: 'write' }
export const RbacActionSchema = z.object({
  resource: z.string(),
  action: z.enum(['read', 'write']),
});
export type RbacAction = z.infer<typeof RbacActionSchema>;

/**
 * Decide whether a role's `can` list permits an action.
 *
 * Rules (matching the prototype's intent):
 *   - '*'                       → permits everything
 *   - '*:read'                  → permits read on any resource
 *   - '<resource>'              → permits both read and write on that resource
 *   - '<resource>:read'         → permits read on that resource only
 *   - '<resource>:write'        → permits write on that resource only
 */
export function rolePermits(can: readonly string[], action: RbacAction): boolean {
  for (const rule of can) {
    if (rule === '*') return true;
    if (rule === '*:read' && action.action === 'read') return true;
    if (rule === action.resource) return true;
    if (rule === `${action.resource}:read` && action.action === 'read') return true;
    if (rule === `${action.resource}:write` && action.action === 'write') return true;
  }
  return false;
}
